import { useState } from 'react';
import { Button, Input } from '@kollektive/design-system';
import { Plus, Search, X } from 'lucide-react';
import { useTokens } from '@/hooks/useTokens';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  TokenTable,
  GenerateTokenModal,
  TokenCreatedModal,
  RevokeTokenDialog,
} from '@/components/tokens';
import type { UserToken, CreateTokenResponse, ExpiryOption } from '@/types';
import styles from './TokensPage.module.css';

export function TokensPage() {
  const { tokens, isLoading, error, refresh, createToken, revokeToken, bulkRevokeTokens, revealToken, isCreating, isRevoking } = useTokens();
  const { currentUser } = useCurrentUser();

  // Modal/dialog state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [createdToken, setCreatedToken] = useState<CreateTokenResponse | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = useState<UserToken | null>(null);
  const [tokensToRevoke, setTokensToRevoke] = useState<UserToken[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleCreateToken = async (name: string, expiry: ExpiryOption) => {
    const response = await createToken({ name, expiry });
    setShowGenerateModal(false);
    setCreatedToken(response);
  };

  const handleCreatedModalClose = () => {
    setCreatedToken(null);
  };

  const handleRevokeClick = (token: UserToken) => {
    setTokenToRevoke(token);
  };

  const handleRevokeConfirm = async () => {
    if (tokenToRevoke) {
      await revokeToken(tokenToRevoke.id);
      setTokenToRevoke(null);
    }
  };

  const handleRevokeCancel = () => {
    setTokenToRevoke(null);
    setTokensToRevoke([]);
  };

  const handleBulkRevoke = (tokens: UserToken[]) => {
    setTokensToRevoke(tokens);
  };

  const handleBulkRevokeConfirm = async () => {
    if (tokensToRevoke.length > 0) {
      await bulkRevokeTokens(tokensToRevoke.map((t) => t.id));
      setTokensToRevoke([]);
    }
  };

  // Determine which dialog to show
  const showRevokeDialog = tokenToRevoke !== null || tokensToRevoke.length > 0;
  const revokeDialogToken = tokenToRevoke ?? tokensToRevoke[0];
  const isBulkRevoke = tokensToRevoke.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>MCP Tokens</h1>
          <p className={styles.subtitle}>
            Manage access tokens for your AI tools
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchWrapper}>
            <Input
              placeholder="Search tokens..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
              iconLeft={<Search size={16} />}
              iconRight={
                searchQuery ? (
                  <button
                    className={styles.clearButton}
                    onClick={() => setSearchQuery('')}
                    type="button"
                  >
                    <X size={14} />
                  </button>
                ) : undefined
              }
              className={styles.searchInput}
            />
          </div>
          <Button
            variant="primary"
            onClick={() => setShowGenerateModal(true)}
            title="Generate Token"
            className={styles.iconButton}
          >
            <Plus size={20} />
          </Button>
        </div>
      </div>

      <TokenTable
        tokens={tokens}
        currentUserId={currentUser?.user_id ?? ''}
        currentUserRole={currentUser?.role ?? 'viewer'}
        isLoading={isLoading}
        error={error}
        onRetry={refresh}
        onRevoke={handleRevokeClick}
        onBulkRevoke={handleBulkRevoke}
        onRevealToken={revealToken}
        isRevoking={isRevoking}
        searchQuery={searchQuery}
      />

      {/* Generate Token Modal */}
      <GenerateTokenModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleCreateToken}
        isLoading={isCreating}
      />

      {/* Token Created Modal */}
      {createdToken && (
        <TokenCreatedModal
          token={createdToken}
          onClose={handleCreatedModalClose}
        />
      )}

      {/* Revoke Token Dialog */}
      {showRevokeDialog && revokeDialogToken && (
        <RevokeTokenDialog
          token={revokeDialogToken}
          tokenCount={isBulkRevoke ? tokensToRevoke.length : 1}
          isOpen={true}
          onClose={handleRevokeCancel}
          onConfirm={isBulkRevoke ? handleBulkRevokeConfirm : handleRevokeConfirm}
          isLoading={isRevoking}
        />
      )}
    </div>
  );
}
